import React, { useEffect, useState } from "react";

import { getCommonConfig } from "@/services/commonconfig";

import { getRoles, saveAllProgramsVisibility } from "../services/allProgramsTabService";

const AllProgramsTabConfig: React.FC = () => {
  const [roles, setRoles] = useState<any[]>([]);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rolesData, config] = await Promise.all([getRoles(), getCommonConfig()]);

      setRoles(rolesData);

      let parsedConfig: Record<string, boolean> = {};

      try {
        parsedConfig = JSON.parse(config?.AllProgramsVisibility || "{}");
      } catch {
        parsedConfig = {};
      }

      const initialMap: Record<string, boolean> = {};

      rolesData.forEach((r: any) => {
        initialMap[r.role_code] =
          parsedConfig[r.role_code] !== undefined ? parsedConfig[r.role_code] : true;
      });

      setVisibility(initialMap);
      setInitial(JSON.stringify(initialMap));
    } catch (err) {
      console.error("Failed to load data", err);
    }
  };

  const setRoleVisibility = (roleCode: string, value: boolean) => {
    setVisibility((prev) => ({
      ...prev,
      [roleCode]: value,
    }));
  };

  const hasChanges = initial !== "" && JSON.stringify(visibility) !== initial;

  const handleSave = async () => {
    setSaving(true);

    try {
      await saveAllProgramsVisibility(visibility, "Admin");
      setInitial(JSON.stringify(visibility));
    } catch {
      alert("Save failed");
    }

    setSaving(false);
  };

  const handleCancel = () => {
    setVisibility(JSON.parse(initial));
  };

  return (
    <div className="border border-gray-200 rounded-md bg-white">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-800">All Programs Tab Configuration</h2>
        <p className="text-xs text-gray-500 mt-1">
          Control visibility of the All Programs tab for each role
        </p>
      </div>

      <div className="p-5 space-y-4">
        {roles.map((role) => (
          <div key={role.role_id} className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {role.role_name}
              <span className="ml-2 text-xs text-gray-400">({role.role_code})</span>
            </div>

            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              <button
                className={`px-4 py-1.5 text-xs ${
                  visibility[role.role_code]
                    ? "bg-[var(--color-brand)]  text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
                onClick={() => setRoleVisibility(role.role_code, true)}
              >
                Show
              </button>

              <button
                className={`px-4 py-1.5 text-xs border-l border-gray-300 ${
                  !visibility[role.role_code]
                    ? "bg-[var(--color-brand)]  text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
                onClick={() => setRoleVisibility(role.role_code, false)}
              >
                Hide
              </button>
            </div>
          </div>
        ))}

        <div className="flex justify-end gap-2 items-center pt-4 border-t border-gray-200">
          {hasChanges && (
            <span className="text-orange-600 bg-orange-50 border border-orange-300 px-2 py-0.5 rounded text-xs">
              Unsaved
            </span>
          )}

          <button
            className="bg-gray-100 px-3 py-1 rounded text-xs"
            onClick={handleCancel}
            disabled={!hasChanges}
          >
            Cancel
          </button>

          <button
            className="bg-[var(--color-brand)]  text-white px-3 py-1 rounded text-xs"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllProgramsTabConfig;
